import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Save, Server, Key, TestTube2, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff, Send, MessageCircle, Coins, FlaskConical } from "lucide-react";

function URLField({ label, value, onChange, icon: Icon, testid }) {
  return (
    <div>
      <label className="tiny-label flex items-center gap-1.5 mb-2"><Icon className="w-3 h-3" /> {label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="input-soft font-mono"
      />
    </div>
  );
}

export default function AdminSettings() {
  const [form, setForm] = useState({
    bulk_url: "",
    private_url: "",
    credit_url: "",
    default_username: "",
    default_password: "",
    default_originator: "",
    test_mode: false,
  });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setForm({
        bulk_url: data.bulk_url || "",
        private_url: data.private_url || "",
        credit_url: data.credit_url || "",
        default_username: data.default_username || "",
        default_password: data.default_password || "",
        default_originator: data.default_originator || "",
        test_mode: !!data.test_mode,
      });
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/settings/test");
      const okMatch = (data.raw || "").toLowerCase().includes("kontor") || (data.raw || "").toUpperCase().startsWith("OK");
      setTestResult({ ok: okMatch, data });
    } catch (e) {
      setTestResult({ ok: false, data: { raw: fmtErr(e?.response?.data?.detail), endpoint: "—" } });
    } finally {
      setTesting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="tiny-label mb-2">Sistem</div>
        <h1 className="font-display font-black text-4xl text-slate-900">Gateway Ayarları</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">E-Telekom XML API endpoint adresleri. Her bir uç nokta için URL&apos;yi ayrı ayrı düzenleyebilirsiniz.</p>
      </div>

      <form onSubmit={save} className="card-soft p-6 sm:p-8 max-w-3xl space-y-6" data-testid="settings-form">
        <div className="space-y-4">
          <div className="tiny-label">API Endpoint&apos;leri</div>
          <URLField label="Bulk SMS URL (1 mesaj → N numara)" value={form.bulk_url} onChange={(v) => setForm({ ...form, bulk_url: v })} icon={Send} testid="settings-bulk-url" />
          <URLField label="Private SMS URL (N×N kişiselleştirilmiş)" value={form.private_url} onChange={(v) => setForm({ ...form, private_url: v })} icon={MessageCircle} testid="settings-private-url" />
          <URLField label="Credit/Kredi Sorgu URL" value={form.credit_url} onChange={(v) => setForm({ ...form, credit_url: v })} icon={Coins} testid="settings-credit-url" />
        </div>

        <div className="border-t border-turq-100 pt-6 space-y-4">
          <div className="tiny-label">Varsayılan Giriş Bilgileri (Kredi sorgusu için)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="tiny-label flex items-center gap-1.5 mb-2"><Key className="w-3 h-3" /> Kullanıcı Adı</label>
              <input
                type="text"
                value={form.default_username}
                onChange={(e) => setForm({ ...form, default_username: e.target.value })}
                placeholder="testapi"
                data-testid="settings-username"
                className="input-soft font-mono"
              />
            </div>
            <div>
              <label className="tiny-label flex items-center gap-1.5 mb-2"><Key className="w-3 h-3" /> Şifre</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={form.default_password}
                  onChange={(e) => setForm({ ...form, default_password: e.target.value })}
                  placeholder="••••••"
                  data-testid="settings-password"
                  className="input-soft font-mono pr-10"
                />
                <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="tiny-label block mb-2">Varsayılan Originator</label>
              <input
                type="text"
                value={form.default_originator}
                onChange={(e) => setForm({ ...form, default_originator: e.target.value })}
                maxLength={11}
                placeholder="ETELEKOM"
                data-testid="settings-originator"
                className="input-soft font-mono uppercase"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-turq-100 pt-6">
          <label className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all ${form.test_mode ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-200"}`} data-testid="test-mode-toggle">
            <input
              type="checkbox"
              checked={form.test_mode}
              onChange={(e) => setForm({ ...form, test_mode: e.target.checked })}
              className="mt-0.5 w-5 h-5 accent-amber-500"
              data-testid="test-mode-checkbox"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <FlaskConical className={`w-4 h-4 ${form.test_mode ? "text-amber-700" : "text-slate-500"}`} />
                <span className={`text-sm font-bold ${form.test_mode ? "text-amber-900" : "text-slate-900"}`}>
                  Test Modu {form.test_mode ? "AÇIK" : "Kapalı"}
                </span>
                <code className={`ml-auto text-[10px] font-mono px-2 py-0.5 rounded ${form.test_mode ? "bg-amber-200 text-amber-900" : "bg-slate-200 text-slate-600"}`}>
                  &lt;TEST&gt;{form.test_mode ? "1" : "0"}&lt;/TEST&gt;
                </code>
              </div>
              <p className="text-xs text-slate-600">
                {form.test_mode
                  ? "XML paketine <TEST>1</TEST> eklenir. SMS gerçekten gönderilmez, gateway sadece doğrulama yapar — kredi düşmez."
                  : "Gerçek mod: SMS&apos;ler gerçekten gönderilir ve kredi düşer. Test için açın."}
              </p>
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between pt-5 border-t border-turq-100">
          <button
            type="button"
            onClick={runTest}
            disabled={testing}
            data-testid="settings-test"
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-turq-800 border border-turq-200 rounded-xl hover:bg-turq-50 disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
            Bağlantıyı Test Et (Kredi Sorgusu)
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-save"
            className="flex items-center gap-2 btn-primary text-sm font-semibold px-6 py-2.5 rounded-xl disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Kaydet
          </button>
        </div>

        {testResult && (
          <div className={`border rounded-xl p-4 ${testResult.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="test-result">
            <div className="flex items-center gap-2 mb-2">
              {testResult.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-700" /> : <AlertTriangle className="w-4 h-4 text-red-700" />}
              <span className={`text-sm font-semibold ${testResult.ok ? "text-emerald-800" : "text-red-800"}`}>
                {testResult.ok ? "Bağlantı başarılı" : "Gateway hata döndü"}
              </span>
            </div>
            <pre className={`text-xs font-mono whitespace-pre-wrap break-all ${testResult.ok ? "text-emerald-900" : "text-red-900"}`}>{testResult.data.raw}</pre>
            <div className="text-[10px] text-slate-500 mt-2 font-mono">↳ {testResult.data.endpoint}</div>
          </div>
        )}
      </form>
    </AdminLayout>
  );
}
